// services/localFolder/LocalFolderService.ts
import { BaseService } from "@services/BaseService";
import { Creator, ServerFile } from "@shared/interfaces/ProjectsFiles";

/**
 * Types aligned with backend schemas (LocalFolderInput/Output).
 * Note: project_id is the project's public_id string at the API boundary.
 */
export interface CreateLocalFolderRequest {
  name: string;
  project_id: string; // public_id
  parent_id?: number | null;
}

export interface UpdateLocalFolderRequest {
  name?: string;
  parent_id?: number | null;
}

export interface LocalFolder {
  id: number;
  name: string;
  project_id: string; // public_id exposed by API
  parent_id: number | null;
  creator_id: number;
  created_at: string; // ISO string
  creator: Creator;
}

export interface LocalFolderBreadcrumbItem {
  id: number;
  name: string;
}

export interface LocalFolderView {
  project_id: string;
  parent_id: number | null;
  name: string;
  folder: LocalFolder;
  files: ServerFile[];
  breadcrumbs: LocalFolderBreadcrumbItem;
}

/**
 * LocalFolderService provides CRUD + navigation helpers
 * for project local folders. Routes mirror the backend router:
 *
 * POST   /local_folders
 * GET    /local_folders/{folder_id}
 * PATCH  /local_folders/{folder_id}
 * DELETE /local_folders/{folder_id}
 * GET    /local_folders/{folder_id}/breadcrumbs
 * GET    /local_folders/{folder_id}/tree
 * GET    /local_folders/projects/{project_public_id}/children?parent_id={id}
 */
class LocalFolderService extends BaseService {
  private readonly base = "/projects/local-folders" as const;

  /**
   * Create a new local folder (root if parent_id is omitted/null).
   */
  create(payload: CreateLocalFolderRequest, projectId: string) {
    return this.post(`/projects/${projectId}/local-folders/`, payload);
  }

  /**
   * Get a local folder by id.
   */
  getById(folderId: number, projectId: string) {
    return this.get(`/projects/${projectId}/local-folders/${folderId}`);
  }

  /**
   * Update (rename or move) a local folder.
   */
  update(
    folderId: number,
    updates: UpdateLocalFolderRequest,
    projectId: string
  ) {
    return this.patch(
      `/projects/${projectId}/local-folders/${folderId}`,
      updates
    );
  }

  /**
   * Delete a local folder.
   */
  remove(folderIds: number[], projectId: string) {
    return this.delete(
      `/projects/${projectId}/local-folders?ids=${folderIds.join(",")}`
    );
  }

  /**
   * Get breadcrumb path for a folder.
   */
  getBreadcrumbs(folderId: number, projectId: string) {
    return this.get(
      `/projects/${projectId}/local-folders/${folderId}/breadcrumbs`
    );
  }

  getFolderView(projectId: string, folderId: number, page = 1, pagesize = 10) {
    return this.get(
      `/projects/${projectId}/local-folders/${folderId}/view?page=${page}&pagesize=${pagesize}`
    );
  }
}

export const localFolderService = new LocalFolderService();
